// import { Carousel } from "@material-tailwind/react";
import { useState, useEffect, useMemo, useCallback, memo, lazy, Suspense, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronUp, Eye } from "lucide-react";
// import './Homepage.css'; // Import the CSS file
import {
  MapPin,
  Building,
  FileText,
  Phone,
  Mail,
  Clock,
  Calendar,
  ChevronRight,
  Menu,
  X,
  Search,
  ExternalLink,
  Briefcase,
  TrendingUp,
  ChevronDown,
  ArrowRight,
  Users,
  Globe,
  Check,
  Play,
  Pause,
  Filter,
  ZoomIn,
  Navigation,
} from "lucide-react";
import apiService from "../config"; // if you're exporting it from config.js
import { useLanguage } from "../StoreContext/LanguageContext"; // Import language hook
import RecentPublications from "./RecentPublications";
import ViewDocumentPublic from "./ViewDocumentPublic";

// Import your Login component and API service (lazy loaded)
const Login = lazy(() => import("../Pages/Login"));

// Loading component for lazy loaded modules
const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-8">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);


function ContactTable({ language, translations }) {
  const [contacts, setContacts] = useState([]);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState(null);
  const [contactPage, setContactPage] = useState(1);
  const contactPerPage = 5;

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setContactLoading(true);
        const response = await apiService.getContactDetails();

        if (response.status === 200) {
          setContacts(response.data);
        } else {
          setContactError("Failed to load contact details.");
        }
      } catch (err) {
        setContactError("Unable to fetch contact details.");
      } finally {
        setContactLoading(false);
      }
    };
    fetchContacts();
  }, []);

  const totalContactPages = Math.ceil(contacts.length / contactPerPage);
  const paginatedContacts = contacts.slice(
    (contactPage - 1) * contactPerPage,
    contactPage * contactPerPage
  );

  if (contactLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-gray-600">Loading contacts...</span>
      </div>
    );
  }

  if (contactError) {
    return (
      <div className="text-center py-8 text-red-500 bg-red-50 rounded-lg border border-red-200">
        {contactError}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200">
      <table className="min-w-full bg-white text-sm">
        <thead>
          <tr className="bg-yellow-500 text-white text-left">
            <th className="px-5 py-3 font-semibold">#</th>
            <th className="px-5 py-3 font-semibold">Name</th>
            <th className="px-5 py-3 font-semibold">Designation</th>
            <th className="px-5 py-3 font-semibold">Email</th>
            <th className="px-5 py-3 font-semibold">Mobile</th>
            <th className="px-5 py-3 font-semibold">Address</th>
          </tr>
        </thead>
        <tbody>
          {paginatedContacts.map((contact, index) => (

            <tr
              key={contact.contact_id}
              className={`border-b border-gray-100 hover:bg-yellow-50 transition-colors duration-150 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
            >
              <td className="px-5 py-4 text-gray-500 font-medium">
                {(contactPage - 1) * contactPerPage + index + 1}
              </td>
              <td className="px-5 py-4 text-gray-800 font-semibold whitespace-nowrap">
                {contact.name}
              </td>
              <td className="px-5 py-4 text-gray-600">{contact.designation}</td>
              <td className="px-5 py-4">
                {contact.email && (
                  <div className="flex items-center gap-2">
                    <Mail size={14} />
                    <span>{contact.email}</span>
                  </div>
                )}
              </td>
              <td className="px-5 py-4">
                {contact.mobile && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <span>{contact.mobile}</span>
                  </div>
                )}
              </td>
              <td className="px-5 py-4 text-gray-600 max-w-xs">{contact.address}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalContactPages > 1 && (
        <div className="flex justify-between items-center px-5 py-4 bg-gray-50 border-t border-gray-200">
          <span className="text-base text-gray-500">
            Page {contactPage} of {totalContactPages} &nbsp;({contacts.length} records)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setContactPage((p) => Math.max(1, p - 1))}
              disabled={contactPage === 1}
              className={`px-4 py-2 rounded-lg text-base font-medium transition ${contactPage === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-yellow-500 text-white hover:bg-yellow-600"
                }`}
            >
              ← Prev
            </button>
            <button
              onClick={() => setContactPage((p) => Math.min(totalContactPages, p + 1))}
              disabled={contactPage === totalContactPages}
              className={`px-4 py-2 rounded-lg text-base font-medium transition ${contactPage === totalContactPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-yellow-500 text-white hover:bg-yellow-600"
                }`}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BuldhanaHomepage() {
  const [showLanguage, setShowLanguage] = useState(false);
  const navigate = useNavigate();
  const { language, changeLanguage, translations } = useLanguage();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const location = useLocation();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [allLoadedProjects, setAllLoadedProjects] = useState({}); // Cache all loaded pages
  const perPage = 20;

  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const hasFetchedPages = useRef(new Set());

  const languageCodeMap = {
    EN: "en",
    HI: "hi",
    MR: "mr",
    ES: "es",
  };
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    hectaresAcquired: 0,
    totalCompensation: 0,
    compensationPaid: 0,
  });

  const finalStats = {
    totalProjects: 183,
    completedProjects: 79,
    hectaresAcquired: 1240,
    totalCompensation: 256,
    compensationPaid: 187,
  };

  const records = [
    {
      id: 1,
      title: "Direct purchase for construction of access road to pump house of phase no. 02 of Jigaon project's Upsa Irrigation Scheme no. 01.",
      description: "Direct purchase for construction of access road to pump house of phase no. 02 of Jigaon project's Upsa Irrigation Scheme no. 01.",
      startDate: "05/02/2026",
      endDate: "05/03/2026",
      file: { name: "document1.pdf", size: "1.2 MB" }
    },
    {
      id: 2,
      title: "Land Acquisition Case No. 02/202-24 Village. Hingne Gawhad (Old Village Thana) Taluka.Nandura Dist. Buldhana",
      description: "Final order publication regarding Land Acquisition Case No. 02/202-24 for village Hingne Gawhad.",
      startDate: "04/02/2026",
      endDate: "04/05/2026",
      file: { name: "order_hingne.pdf", size: "2.4 MB" }
    },
    {
      id: 3,
      title: "Notification for Jelgaon Irrigation Project Phase 3 Expansion",
      description: "Public notice regarding land survey for the upcoming phase 3 expansion of the Jelgaon Irrigation Project.",
      startDate: "28/01/2026",
      endDate: "28/02/2026",
      file: { name: "notification_jelgaon_ph3.pdf", size: "850 KB" }
    },
    {
      id: 4,
      title: "Tender Notice: Construction of Community Hall in Maldhoni",
      description: "Inviting sealed tenders for the construction of a new community hall in Maldhoni village.",
      startDate: "15/01/2026",
      endDate: "15/02/2026",
      file: { name: "tender_maldhoni_hall.pdf", size: "3.1 MB" }
    },
    {
      id: 5,
      title: "Revised Compensation Structure for Highway Expansion Project affecting Sindkhed Raja",
      description: "Detailed breakdown of the revised compensation rates for land acquisition related to the highway expansion.",
      startDate: "10/01/2026",
      endDate: "10/04/2026",
      file: { name: "compensation_highway_sindkhed.pdf", size: "1.8 MB" }
    },
    {
      id: 6,
      title: "Annual Water Quality Report 2025 - Buldhana District",
      description: "Comprehensive report on water quality analysis across all talukas in Buldhana district for the year 2025.",
      startDate: "01/01/2026",
      endDate: "31/12/2026",
      file: { name: "water_quality_report_2025.pdf", size: "5.6 MB" }
    },
    {
      id: 7,
      title: "Draft Development Plan for Shegaon Pilgrimage Zone",
      description: "Proposed draft development plan for the area surrounding the Shegaon temple complex for public review.",
      startDate: "15/12/2025",
      endDate: "15/01/2026",
      file: { name: "shegaon_dev_plan_draft.pdf", size: "12 MB" }
    },
    {
      id: 8,
      title: "Auction Notice for Scrap Material at District Collector Office",
      description: "Notice for public auction of old vehicles and office scrap material located at the Collector Office premises.",
      startDate: "01/12/2025",
      endDate: "10/12/2025",
      file: { name: "auction_scrap_dec2025.pdf", size: "450 KB" }
    },
    {
      id: 9,
      title: "Employment Exchange Registration Drive - Jan 2026",
      description: "Schedule and guidelines for the special employment exchange registration drive to be held in January 2026.",
      startDate: "20/11/2025",
      endDate: "31/01/2026",
      file: { name: "employment_drive_jan2026.pdf", size: "600 KB" }
    },
    {
      id: 10,
      title: "Land Acquisition Award: Lonar Lake Conservation Project",
      description: "Declaration of award for lands acquired for the buffer zone of the Lonar Lake Conservation Project.",
      startDate: "10/11/2025",
      endDate: "10/12/2025",
      file: { name: "lonar_award_notice.pdf", size: "1.5 MB" }
    },
    {
      id: 11,
      title: "Scholarship Scheme for Meritorious Students 2025-26",
      description: "Application details and eligibility criteria for district-level merit scholarships for the academic year 2025-26.",
      startDate: "01/10/2025",
      endDate: "30/11/2025",
      file: { name: "scholarship_2025_26.pdf", size: "900 KB" }
    },
    {
      id: 12,
      title: "Public Health Advisory: Dengue Prevention Measures",
      description: "Official advisory issued by the District Health Officer regarding Dengue prevention and control measures.",
      startDate: "15/09/2025",
      endDate: "15/10/2025",
      file: { name: "dengue_advisory_2025.pdf", size: "300 KB" }
    },
    {
      id: 13,
      title: "Voter List Revision Programme 2025 - Summary Revision",
      description: "Schedule for the special summary revision of electoral rolls for all assembly constituencies in the district.",
      startDate: "01/09/2025",
      endDate: "30/09/2025",
      file: { name: "voter_list_revision_2025.pdf", size: "1.1 MB" }
    },
    {
      id: 14,
      title: "Tender for Supply of Stationery to ZP Schools",
      description: "E-tender notice for the supply of educational stationery kits to Zilla Parishad schools.",
      startDate: "10/08/2025",
      endDate: "25/08/2025",
      file: { name: "tender_stationery_zp.pdf", size: "2.0 MB" }
    },
    {
      id: 15,
      title: "Disaster Management Plan 2025 - Monsoon Preparedness",
      description: "District Disaster Management Plan outlining preparedness and response strategies for the 2025 monsoon season.",
      startDate: "01/06/2025",
      endDate: "30/09/2025",
      file: { name: "disaster_plan_monsoon_2025.pdf", size: "4.5 MB" }
    },
    {
      id: 16,
      title: "Recruitment Notice: Data Entry Operators",
      description: "Walk-in interview notice for the temporary posts of Data Entry Operators for the election department.",
      startDate: "15/05/2025",
      endDate: "20/05/2025",
      file: { name: "recruitment_deo_2025.pdf", size: "500 KB" }
    },
    {
      id: 17,
      title: "Tree Plantation Drive 'Green Buldhana' - Guidelines",
      description: "Guidelines and targets for the district-wide tree plantation drive scheduled for July 2025.",
      startDate: "01/05/2025",
      endDate: "31/07/2025",
      file: { name: "green_buldhana_guidelines.pdf", size: "1.3 MB" }
    }
  ];

  // Helper functions - Memoized to prevent recreation on every render (MUST be declared before useEffects)
  const getProjectStatus = useCallback((isActive) => {
    if (isActive === true) return "Active";
    if (isActive === false) return "Completed";
    return "Planning";
  }, []);

  const getProjectCompletion = useCallback((isActive) => {
    if (isActive === true) return `${Math.floor(Math.random() * 60) + 20}%`;
    if (isActive === false) return "100%";
    return `${Math.floor(Math.random() * 30) + 5}%`;
  }, []);

  const getProjectProgress = useCallback((isActive) => {
    if (isActive === true) return Math.floor(Math.random() * 60) + 20;
    if (isActive === false) return 100;
    return Math.floor(Math.random() * 30) + 5;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Date not set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  }, []);

  const getProjectDescription = useCallback((type, division) => {
    const descriptions = {
      Agricultural: `Agricultural development project in ${division || "district"
        } division for enhanced farming infrastructure`,
      "Road Infrastructure": `Road development and connectivity improvement project in ${division || "district"
        } division`,
      "Water Resources": `Water management and irrigation project serving ${division || "district"
        } area`,
      Industrial: `Industrial development initiative in ${division || "district"
        } division`,
      Healthcare: `Healthcare infrastructure development in ${division || "district"
        } division`,
      Education: `Educational facility development project in ${division || "district"
        } division`,
      Energy: `Renewable energy project implementation in ${division || "district"
        } division`,
    };
    return (
      descriptions[type] ||
      `Development project in ${division || "district"
      } division for regional growth`
    );
  }, []);

  const getProjectTimelineText = useCallback((createdDate) => {
    if (!createdDate) return "2024-2026";
    const startYear = new Date(createdDate).getFullYear();
    const endYear = startYear + Math.floor(Math.random() * 3) + 1;
    return `${startYear}-${endYear}`;
  }, []);

  const getEstimatedCompletion = useCallback((project) => {
    const dates = ["Dec 2024", "Mar 2025", "Jun 2025", "Sep 2025", "Dec 2025"];
    return dates[Math.floor(Math.random() * dates.length)];
  }, []);

  const getProjectTags = useCallback((project) => {
    const allTags = [
      "Infrastructure",
      "Development",
      "Rural",
      "Urban",
      "Sustainable",
      "Modern",
      "Innovation",
    ];
    return allTags.slice(0, Math.floor(Math.random() * 3) + 2);
  }, []);

  // Load cached projects from localStorage on mount
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem('homepage_projects_cache');
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const cacheAge = Date.now() - parsed.timestamp;
        const maxAge = 10 * 60 * 1000; // 10 minutes

        if (cacheAge < maxAge) {
          setAllLoadedProjects(parsed.pages || {});
          setTotalPages(parsed.totalPages || 1);
          setTotalProjects(parsed.totalProjects || 0);

          // Load page 1 if available
          if (parsed.pages && parsed.pages['1']) {
            setProjects(parsed.pages['1']);
            setLoading(false);
            hasFetchedPages.current.add(1);
          }
        } else {
          // Cache expired, clear it
          localStorage.removeItem('homepage_projects_cache');
        }
      }
    } catch (err) {
      console.error('Error loading cached projects:', err);
      localStorage.removeItem('homepage_projects_cache');
    }
  }, []);

  // Save to localStorage whenever allLoadedProjects changes
  useEffect(() => {
    if (Object.keys(allLoadedProjects).length > 0) {
      try {
        const cacheData = {
          pages: allLoadedProjects,
          totalPages,
          totalProjects,
          timestamp: Date.now()
        };
        localStorage.setItem('homepage_projects_cache', JSON.stringify(cacheData));
      } catch (err) {
        console.error('Error saving to localStorage:', err);
      }
    }
  }, [allLoadedProjects, totalPages, totalProjects]);

  // Fetch projects data from API - Optimized with pagination and caching
  useEffect(() => {
    // Prevent duplicate calls
    if (isFetchingRef.current || hasFetchedPages.current.has(currentPage)) {
      // If page is already loaded, just display it from cache
      if (allLoadedProjects[currentPage]) {
        setProjects(allLoadedProjects[currentPage]);
        setLoading(false);
      }
      return;
    }

    const fetchProjects = async () => {
      try {
        isFetchingRef.current = true;
        setLoading(true);

        const response = await apiService.getAllProjectsPaginate(currentPage, perPage);

        // Handle paginated response
        const projectsData = Array.isArray(response.data)
          ? response.data
          : [response.data];

        // Update pagination info
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalProjects(response.pagination.totalResults);
        }

        // Map API response to component structure with enhanced details
        const mappedProjects = projectsData.map((project, index) => ({
          id: project.project_id,
          title: project.project_name || `Project ${project.project_id}`,
          category: project.project_type || "Development",
          image:
            project.project_banner ||
            `\\home image\\project-${(index % 4) + 1}.jpg`,
          status: getProjectStatus(project.isactive),
          budget: project.total_budget ? `₹${project.total_budget}` : "₹0",
          completion:
            project.percentage || getProjectCompletion(project.isactive),
          description: getProjectDescription(
            project.project_type,
            project.project_division
          ),
          timeline: getProjectTimelineText(project.cre_usr_dt),
          division: project.project_division,
          village: project.land_village,
          tehsil: project.land_tehsil,
          acquisitionNo: project.land_acquisition_no,
          proposalNo: project.acquisition_proposal_no,
          inchargeId: project.project_incharge_usr_id,
          // Enhanced fields for display
          location:
            `${project.eng_village_name || ""}, ${project.eng_tehsil_name || ""
              }`.replace(/^,\s*|,\s*$/g, "") || "Location not specified",
          area: `${project.no_of_budharak || 0} budharak`,
          progress:
            parseInt(project.percentage) ||
            getProjectProgress(project.isactive),
          date: formatDate(project.last_upd_dt),
          stakeholders: project.no_of_budharak || 0,
          type: project.project_type,
          landAcquisitionNo: project.land_acquisition_no,
          district: project.land_district,
          adminOrderNo: project.administrative_order_no,
          adminOrderDate: project.administrative_order_date,
          isActive: project.isactive,
          createdDate: project.cre_usr_dt,
          updatedDate: project.last_upd_dt,
          // Additional enhanced data
          estimatedCompletion: getEstimatedCompletion(project),
          tags: getProjectTags(project),
          manager: `Manager ${index + 1}`,
          contact: `+91-${9000000000 + index}`,
        }));

        // Update current projects display
        setProjects(mappedProjects);

        // Save to cache
        setAllLoadedProjects(prev => ({
          ...prev,
          [currentPage]: mappedProjects
        }));

        // Mark page as fetched
        hasFetchedPages.current.add(currentPage);

        setError(null);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Failed to load projects. Please try again later.");
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, allLoadedProjects]);

  // Preload next page in background for better UX
  useEffect(() => {
    const preloadNextPage = async () => {
      const nextPage = currentPage + 1;

      // Only preload if:
      // 1. Not already loaded
      // 2. Next page exists
      // 3. Not currently loading
      if (
        !hasFetchedPages.current.has(nextPage) &&
        nextPage <= totalPages &&
        !loading &&
        !isFetchingRef.current
      ) {
        try {
          const response = await apiService.getAllProjectsPaginate(nextPage, perPage);

          if (response.data) {
            const projectsData = Array.isArray(response.data) ? response.data : [response.data];

            const mappedProjects = projectsData.map((project, index) => ({
              id: project.project_id,
              title: project.project_name || `Project ${project.project_id}`,
              category: project.project_type || "Development",
              image: project.project_banner || `\\home image\\project-${(index % 4) + 1}.jpg`,
              status: getProjectStatus(project.isactive),
              budget: project.total_budget ? `₹${project.total_budget}` : "₹0",
              completion: project.percentage || getProjectCompletion(project.isactive),
              description: getProjectDescription(project.project_type, project.project_division),
              timeline: getProjectTimelineText(project.cre_usr_dt),
              division: project.project_division,
              village: project.land_village,
              tehsil: project.land_tehsil,
              acquisitionNo: project.land_acquisition_no,
              proposalNo: project.acquisition_proposal_no,
              inchargeId: project.project_incharge_usr_id,
              location: `${project.eng_village_name || ""}, ${project.eng_tehsil_name || ""}`.replace(/^,\s*|,\s*$/g, "") || "Location not specified",
              area: `${project.no_of_budharak || 0} budharak`,
              progress: parseInt(project.percentage) || getProjectProgress(project.isactive),
              date: formatDate(project.last_upd_dt),
              stakeholders: project.no_of_budharak || 0,
              type: project.project_type,
              landAcquisitionNo: project.land_acquisition_no,
              district: project.land_district,
              adminOrderNo: project.administrative_order_no,
              adminOrderDate: project.administrative_order_date,
              isActive: project.isactive,
              createdDate: project.cre_usr_dt,
              updatedDate: project.last_upd_dt,
              estimatedCompletion: getEstimatedCompletion(project),
              tags: getProjectTags(project),
              manager: `Manager ${index + 1}`,
              contact: `+91-${9000000000 + index}`,
            }));

            // Cache the preloaded page
            setAllLoadedProjects(prev => ({
              ...prev,
              [nextPage]: mappedProjects
            }));

            hasFetchedPages.current.add(nextPage);
          }
        } catch (err) {
          // Silent fail for preloading
          console.log('Preload next page failed (non-critical):', err);
        }
      }
    };

    // Delay preload slightly to not interfere with current page render
    const timer = setTimeout(preloadNextPage, 1000);
    return () => clearTimeout(timer);
  }, [currentPage, totalPages, loading, allLoadedProjects, getProjectStatus, getProjectCompletion, getProjectDescription, getProjectTimelineText, getProjectProgress, formatDate, getEstimatedCompletion, getProjectTags, perPage]);

  // Function to handle project navigation
  const handleProjectClick = useCallback((project) => {
    // Navigate to land acquisition page with project filter
    navigate("/LandAcquisitionProjects", {
      state: {
        selectedProject: project.id,
        searchTerm: project.title,
      },
    });
  }, [navigate]);

  // Page change handler with smooth scroll
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of projects section smoothly
    const projectsSection = document.getElementById('projects-section');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Function to handle raise objection navigation
  const handleRaiseObjection = useCallback((e) => {
    if (e) {
      e.stopPropagation();
    }
    setShowLoginPopup(true);
  }, []);

  const toggleLoginPopup = useCallback(() => {
    setShowLoginPopup(prev => !prev);
  }, []);

  const handleCloseLogin = useCallback(() => {
    setShowLoginPopup(false);
  }, []);

  useEffect(() => {
    // Check if user came from email verification
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("verified") === "true") {
      setShowLoginPopup(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Animate stats counter on scroll - Throttled for performance
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 100);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Animate stats counter
  useEffect(() => {
    const animateStats = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              startCountAnimation();
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      const statsSection = document.getElementById("stats-section");
      if (statsSection) {
        observer.observe(statsSection);
      }
    };

    animateStats();
  }, []);

  const startCountAnimation = () => {
    const duration = 2000; // 2 seconds
    const frameDuration = 1000 / 60; // 60fps
    const totalFrames = Math.round(duration / frameDuration);

    let frame = 0;
    const countInterval = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;

      setStats({
        totalProjects: Math.floor(progress * finalStats.totalProjects),
        completedProjects: Math.floor(progress * finalStats.completedProjects),
        hectaresAcquired: Math.floor(progress * finalStats.hectaresAcquired),
        totalCompensation: Math.floor(progress * finalStats.totalCompensation),
        compensationPaid: Math.floor(progress * finalStats.compensationPaid),
      });

      if (frame === totalFrames) {
        clearInterval(countInterval);
        setStats(finalStats);
      }
    }, frameDuration);
  };

  // Toggle mobile menu
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleLanguageChange = useCallback((code) => {
    const contextLangCode = languageCodeMap[code] || "en";
    changeLanguage(contextLangCode);
    setShowLanguage(false);
  }, [changeLanguage]);

  // Get the current language code for display
  const getCurrentLanguageCode = useCallback(() => {
    for (const [uiCode, contextCode] of Object.entries(languageCodeMap)) {
      if (contextCode === language) {
        return uiCode;
      }
    }
    return "EN"; // Default
  }, [language]);

  const languageOptions = useMemo(() => [
    { code: "EN", name: "English", flag: "🇺🇸" },
    // { code: "HI", name: "हिंदी", flag: "hi" },
    { code: "MR", name: "मराठी", flag: "mr" },
  ], []);

  const backgroundImages = useMemo(() => [
    "/home image/background1.jpg",
    "/home image/background2.png",
    "/home image/background3.jpg",
    "/home image/background4.jpeg",
    "/home image/background1.jpg",
  ], []);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  return (
    <div className=" flex flex-col min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <div className="relative min-h-screen overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <div className="slideshow-container absolute inset-0"></div>
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="relative z-20">
          <header
            className={`absolute w-full duration-500 z-30  `}
          >
            <div className="mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-center p-3">
                {/* Logo and Title */}
                <div className="flex items-center space-x-3 mb-3 md:mb-0">
                  <img
                    src="\home image\punarbhu.png"
                    alt="Buldhana Landscape"
                    className="w-[100px] h-[100px] md:w-[150px] md:h-[120px] lg:w-[150px] lg:h-[140px] object-contain"
                  />
                </div>
                <div >
                  <p className="text-5xl text-white font-bold text-center">
                    {translations[language].BuldhanaCollector}
                  </p>
                </div>

                {/* Desktop buttons */}
                <div className="hidden md:flex space-x-3">
                  <div className="w-22 h-22 lg:w-24 lg:h-24 xl:w-28 xl:h-28 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center overflow-hidden transform transition hover:scale-105 duration-300 shadow-lg">
                    <img
                      src="/home image/maha-logo.svg"
                      alt="Buldhana Logo"
                      className="w-24 h-24 object-cover"
                    />
                  </div>
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden flex items-center">
                  <button
                    onClick={toggleMenu}
                    className="p-2 focus:outline-none text-gray-700"
                    aria-label="Toggle menu"
                  >
                    {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                  </button>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:block text-white ">
                <ul className="flex justify-center text-sm">
                  <li className="group relative">
                    <a
                      href="/"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].Home1}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>
                  {/* <li className="group relative">
                    <a
                      href="/AboutBuldhanaPage"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].About}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li> */}
                  <li className="group relative">
                    <a
                      href="#contact"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                      onClick={(e) => {
                        e.preventDefault();
                        const targetElement = document.getElementById("contact");
                        if (targetElement) {
                          targetElement.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      {translations[language].Contact}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>
                  <li className="group relative">
                    <a
                      href="#land-acquisition"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                      onClick={(e) => {
                        e.preventDefault();
                        const targetElement = document.getElementById("land-acquisition");
                        if (targetElement) {
                          targetElement.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      {translations[language].notifications}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>

                  <li className="group relative">
                    <a
                      href="/ViewDocumentPublic"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].completedPortel}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>

                  {/* <li className="group relative">
                    <a
                      href="/ViewDocumentPublic"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].ViewNivada}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li> */}

                  {/* <li className="group relative">
                    <a
                      href="#Development"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                      onClick={(e) => {
                        e.preventDefault();
                        const targetElement = document.getElementById("Development");
                        if (targetElement) {
                          targetElement.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      {translations[language].Overview}
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li> */}


                  {/* Login Button */}
                  <li className="group relative">
                    <a
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out cursor-pointer"
                      onClick={toggleLoginPopup}
                    >
                      <span>{translations[language].Login}</span>
                    </a>
                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>

                  {/* Language Selector - Right side of Login */}
                  <li className="group relative">
                    <button
                      onClick={() => setShowLanguage(!showLanguage)}
                      className="flex items-center px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                      aria-label="Switch Language"
                    >
                      <Globe className="h-4 w-4 mr-1 text-white" />
                      <span className="text-sm font-medium">{getCurrentLanguageCode()}</span>
                      <ChevronDown className="h-3 w-3 ml-1 text-white" />
                    </button>

                    {showLanguage && (
                      <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-2xl py-2 z-50 border border-gray-200 animate-scaleIn">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <h3 className="font-medium text-gray-800 flex items-center text-sm">
                            <Globe className="h-4 w-4 mr-2 text-blue-500" />
                            {translations[language]?.language || "Language"}
                          </h3>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {languageOptions.map((lang) => (
                            <button
                              key={lang.code}
                              onClick={() => handleLanguageChange(lang.code)}
                              className={`w-full text-left px-4 py-2 flex items-center space-x-3 hover:bg-blue-50 transition duration-200 ${getCurrentLanguageCode() === lang.code
                                ? "bg-blue-50 font-medium"
                                : ""
                                }`}
                            >
                              <span className="text-lg">{lang.flag}</span>
                              <span className="text-sm text-gray-700">{lang.name}</span>
                              {getCurrentLanguageCode() === lang.code && (
                                <Check className="h-4 w-4 text-blue-500 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="absolute left-0 w-full h-1 bg-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </li>
                </ul>
              </nav>

              {/* Mobile Navigation */}
              <nav
                className={`md:hidden bg-gray-800 text-white absolute w-full left-0 z-20 transform transition-transform duration-300 ease-in-out ${isMenuOpen
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-full opacity-0"
                  }`}
              >
                <ul className="flex flex-col text-sm">
                  {/* <li className="border-b border-gray-700">
                    <a
                      href="/"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].Home}
                    </a>
                  </li> */}
                  {/* <li className="border-b border-gray-700">
                    <a
                      href="/about.js"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].About}
                    </a>
                  </li> */}
                  <li className="border-b border-gray-700">
                    <a
                      href="/PublicationSection.js"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      PublicationSection
                    </a>
                  </li>
                  <li className="border-b border-gray-700">
                    <a
                      href="#Development"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].Overview}
                    </a>
                  </li>
                  <li className="border-b border-gray-700">
                    <a
                      href="#land-acquisition"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].Notifications}
                    </a>
                  </li>
                  <li className="border-b border-gray-700 last:border-0">
                    <a
                      href="#contact"
                      className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                    >
                      {translations[language].Contact}
                    </a>
                  </li>
                  <li className="p-4 space-y-2">
                    <button
                      className="w-full px-4 py-2 border border-gray-300 rounded-full text-white hover:bg-gray-700 transition"
                      onClick={toggleLoginPopup}
                    >
                      {translations[language].Login}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </header>

          {showLoginPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 relative">
                <button
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                  onClick={toggleLoginPopup}
                >
                  <X size={24} />
                </button>
                <div className="mt-2">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Login
                      onLoginSuccess={() => {
                        // setShowLoginPopup(false);
                        navigate('/Dashboard');
                      }}
                      onClose={() => setShowLoginPopup(false)}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          )}

          {/* Hero Section */}
          <div className="relative overflow-hidden pt-[120px]">
            <div className="absolute right-10 top-0 w-1/2 h-full transform -skew-x-12 translate-x-1/4 opacity-20"></div>
            <div className="container mx-auto px-4 py-4 md:py-6 ">
              <div className="flex flex-col md:flex-row  items-center md:items-center gap-10">
                {/* Left Section (Text Only, No Background) */}
                <div className="w-full md:w-[100%] text-center justify-center  md:text-left ">
                  <div>
                    <img
                      src="\home image\logo.png"
                      alt="Punarbhu Logo"
                      className="w-[1800px] h-[400px] object-contain mx-auto"
                    />
                  </div>
                  <p className="text-base md:text-lg leading-relaxed flex flex-wrap text-center justify-center gap-2 mb-2">
                    {/* First line */}
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">P</span>
                      lanned
                    </span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">U</span>
                      tilization,
                    </span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">N</span>
                      eed
                    </span>
                    <span className="text-white">based</span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">A</span>
                      cquisition
                    </span>
                    <span className="text-white">&</span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">R</span>
                      ehabilitation
                    </span>
                  </p>
                  <p className="text-base md:text-lg leading-relaxed flex flex-wrap text-center justify-center gap-2 mb-4">
                    {/* Second line */}
                    <span className="text-white">with</span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">B</span>
                      etter
                    </span>
                    <span className="text-white">and</span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">H</span>
                      umane
                    </span>
                    <span className="text-white">
                      <span className="text-xl font-medium text-white">U</span>
                      pliftment
                    </span>
                  </p>
                  <p className="text-base md:text-lg text-white leading-relaxed flex flex-wrap text-center justify-center">
                    {translations[language].AcquisitionTagline}
                  </p>
                  <p className="text-base md:text-lg text-white leading-relaxed flex flex-wrap text-center justify-center">
                    Version 3.0.50
                  </p>
                </div>
     
                {/* Right Section (Bigger Image) */}
                {/* <div className="w-full md:w-[50%] flex justify-center ">
                  <div className="relative w-full max-w-lg ml-3 ">
                    <img
                      src="\home image\punarbhu.png"
                      alt="Buldhana Landscape"
                      className="w-[400px] h-[400px] object-contain"
                    />
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
        {/* CSS Animations and Slideshow */}
        <style jsx>{`
       .slideshow-container {
         animation: slideshow 20s infinite;
         background-size: cover;
         background-position: center;
         background-repeat: no-repeat;
       }
       
       @keyframes slideshow {
         0%, 18% { 
           background-image: url('/home image/background1.jpg');
         }
         20%, 38% { 
           background-image: url('/home image/background2.jpg');
         }
         40%, 58% { 
           background-image: url('/home image/1.webp');
         }
         60%, 78% { 
           background-image: url('/home image/background9.jpg');
         }
         80%, 100% { 
           background-image: url('/home image/background5.jpg');
         }
       }

       .floating-animation {
         animation: float 0.5s ease-in-out infinite;
       }
       
       @keyframes float {
         0%, 100% { 
           transform: translateY(0px) rotate(0deg); 
         }
         50% { 
           transform: translateY(-20px) rotate(2deg); 
         }
       }

       .animate-fadeIn {
         animation: fadeIn 1s ease-in;
       }

       @keyframes fadeIn {
         from { opacity: 0; transform: translateY(20px); }
         to { opacity: 1; transform: translateY(0); }
       }

       
     `}</style>
      </div>

      <div id="land-acquisition" className="bg-gray-50 p-6">
        <div>
          <RecentPublications records={records} />
        </div>
      </div>

      {/* Clean Development Projects Section with Land Acquisition Navigation */}
      {/* <div id="land-acquisition" className="container mx-auto px-4 py-16"> */}
      {/* <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {translations[language].DevelopmentProjects}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {translations[language].OngoingProjects}
          </p>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4 rounded-full"></div>
        </div> */}

      {/* Clean Controls */}
      {/* <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              {isPlaying ? (
                <Pause size={16} className="mr-2" />
              ) : (
                <Play size={16} className="mr-2" />
              )}
              {isPlaying ? (
                <span>{translations[language].Pause}</span>
              ) : (
                <span>{translations[language].Play}</span>
              )}
            </button>
          </div>
        </div> */}

      {/* Loading State */}
      {/* {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            <span className="ml-3 text-gray-600">
              {translations[language].LoadingProjects}...
            </span>
          </div>
        )} */}

      {/* Error State */}
      {/* {error && (
          <div className="text-center py-16">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Retry
              </button>
            </div>
          </div>
        )} */}



      {/* Pagination Controls */}
      {/* {!loading && !error && totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-12 mb-8">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-lg hover:shadow-xl'
                }`}
            >
              ← Previous
            </button>

            <div className="flex items-center gap-2">
              <span className="text-gray-700 font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <span className="text-gray-500 text-sm">
                ({totalProjects} total projects)
              </span>
            </div>

            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-lg hover:shadow-xl'
                }`}
            >
              Next →
            </button>
          </div>
        )} */}

      {/* View All Projects Button */}
      {/* <div className="text-center mt-8">
          <a
            href="/LandAcquisitionProjects"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-full font-semibold hover:from-yellow-600 hover:to-yellow-700 transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group"
          >
            <span>{translations[language].ViewAllProjects}</span>
            <ArrowRight
              size={20}
              className="ml-3 transform group-hover:translate-x-1 transition-transform duration-300"
            />
          </a>
        </div> */}
      {/* </div> */}

      {/* Add CSS for clean auto-scrolling animation */}
      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
           .animate-scroll {
    animation: scroll var(--scroll-duration) linear infinite;
  }

        

        .animate-scroll:hover {
          animation-play-state: paused;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @keyframes pulse-glow {
          0%,
          100% {
            box-shadow: 0 0 5px rgba(251, 191, 36, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.8);
          }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>

      {/* About Buldhana */}
      <div className="bg-gradient-to-b from-gray-50 to-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Text Section */}
            <div className="w-full md:w-1/2 animate-fadeIn">
              <h2 className="text-3xl font-bold mb-6 text-gray-800">
                <span className="text-yellow-600">
                  {translations[language].AboutDistrict}
                </span>
              </h2>
              <div className="w-20 h-1 bg-yellow-500 mb-6 rounded-full"></div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {translations[language].Aboutdes1}
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                {translations[language].Aboutdes2}
              </p>

              <a
                href="/AboutBuldhanaPage"
                className="px-6 py-3 bg-yellow-500 text-white rounded-lg shadow-md hover:bg-yellow-600 transition duration-300 flex items-center group"
              >
                <span>{translations[language].LearnMore}</span>
                <ExternalLink
                  size={16}
                  className="ml-2 transform group-hover:scale-110 transition-transform"
                />
              </a>
            </div>

            {/* Map Section */}
            <div className="w-full md:w-1/2 animate-fadeIn">
              <div className="relative w-full pb-[75%] overflow-hidden rounded-2xl shadow-xl border border-gray-200 transition-transform duration-500 hover:scale-105">
                <div className="absolute top-0 left-0 w-full h-full bg-gray-200 animate-pulse"></div>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-2xl"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.4940906506516!2d76.17837331490227!3d20.528385886299437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd6fe9842e351f9%3A0x5558ebf64a8946f7!2sBuldhana%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1629530947073!5m2!1sen!2sin"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>

                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-500 rounded-full opacity-20"></div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-yellow-500 rounded-full opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Buldhana District Spotlight */}
      {/* <div id="buldhana" className="container mx-auto px-4 py-16">
      {/* <div id="buldhana" className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{translations[language].DevelopmentTitle}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{translations[language].DevelopmentDesc}</p>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4 rounded-full"></div>
        </div>

       
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition duration-300">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="text-yellow-600" size={24} />
            </div>
            <h3 className="font-semibold text-xl text-gray-800 mb-2">{translations[language].AtAGlance}</h3>
            <p className="text-gray-600">{translations[language].DistrictIntro}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition duration-300">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="text-yellow-600" size={24} />
            </div>
            <h3 className="font-semibold text-xl text-gray-800 mb-2">{translations[language].DevelopmentFocus}</h3>
            <p className="text-gray-600">{translations[language].DevelopmentFocus1}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition duration-300">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="text-yellow-600" size={24} />
            </div>
            <h3 className="font-semibold text-xl text-gray-800 mb-2">{translations[language].ProgressOverview}</h3>
            <p className="text-gray-600">{projects.length} {translations[language].OngoingProjects}</p>
          </div>
        </div>
      </div> */}

      {/* Contact Section */}
      <div id="contact" className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {translations[language].ContactUs}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {translations[language].ContactInfo}
          </p>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4 rounded-full"></div>
        </div>

        <ContactTable language={language} translations={translations} />
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-12 mt-auto">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center overflow-hidden">
                  <img
                    src="\home image\buldana image copy.svg"
                    alt="Buldhana Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-bold">
                    {translations[language].BuldhanaCollector}
                  </h4>
                  <p className="text-xs text-gray-400">
                    {translations[language].GovernmentOfMaharashtra}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                {translations[language].PortalTitle}
              </p>
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition transform hover:scale-110 cursor-pointer">
                  <span className="text-white text-sm">
                    <a href="https://www.facebook.com/sharer/sharer.php?u=https://zpbuldhana.maharashtra.gov.in/en/about-department/introduction/&t=Introduction">
                      f
                    </a>
                  </span>
                </div>
                <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center hover:bg-blue-500 transition transform hover:scale-110 cursor-pointer">
                  <span className="text-white text-sm">
                    <a href="https://x.com/intent/post?url=https%3A%2F%2Fzpbuldhana.maharashtra.gov.in%2Fen%2Fabout-department%2Fintroduction%2F&via=TWITTER_HANDLE&text=Introduction">
                      X
                    </a>
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].QuickLinks}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <ul className="text-sm space-y-3 text-gray-300">
                {[
                  {
                    name: <span>{translations[language].Home1}</span>,
                    href: "/",
                  },
                  {
                    name: <span>PublicationSection</span>,
                    href: "/OpenDashboard",
                  },
                  // {
                  //   name: <span>{translations[language].About}</span>,
                  //   href: "/AboutBuldhanaPage",
                  // },
                  {
                    name: <span>{translations[language].Overview}</span>,
                    href: "#Development",
                  },
                  {
                    name: <span>{translations[language].Notifications}</span>,
                    href: "#land-acquisition",
                  },
                  {
                    name: <span>{translations[language].Contact}</span>,
                    href: "#contact",
                  },
                ].map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="hover:text-yellow-400 cursor-pointer transform hover:translate-x-1 transition-transform duration-300 flex items-center"
                    >
                      <ChevronRight
                        size={14}
                        className="mr-1 text-yellow-500"
                      />
                      <span>{link.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].ImportantResources}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <ul className="text-sm space-y-3 text-gray-300">
                {[
                  <a href="https://www.maharashtra.gov.in/">
                    {translations[language].MaharashtraGovernment}
                  </a>,
                  <a href="https://sahajpranali.safevaults.in/en/login/public">
                    {translations[language].Sahajpranali}
                  </a>,
                  <a href="https://jigaon.in/">
                    {translations[language].Jigaon}
                  </a>,
                  <a href="https://amravatidivision.gov.in/">
                    {translations[language].AmravatiDivision}
                  </a>,
                  <a href="https://aaplesarkar.mahaonline.gov.in/en">
                    {translations[language].AapleSarkar}
                  </a>,
                ].map((resource, index) => (
                  <li
                    key={index}
                    className="hover:text-yellow-400 cursor-pointer transform hover:translate-x-1 transition-transform duration-300 flex items-center"
                  >
                    <ChevronRight size={14} className="mr-1 text-yellow-500" />
                    <span>{resource}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].OfficeHours}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <div className="flex items-center space-x-2 mb-3 text-gray-300 text-sm">
                <Clock size={18} className="text-yellow-400" />
                <span>
                  {translations[language].MondaytoFriday}: 10:00 AM - 5:30 PM
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <Clock size={18} className="text-yellow-400" />
                <span>
                  {translations[language].Saturday}: 10:00 AM - 2:00 PM
                </span>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <p className="text-center md:text-left text-sm text-gray-400">
              © 2025 Government of Maharashtra, Buldhana District. All Rights
              Reserved.
            </p>
            <div className="flex items-center justify-center md:justify-end space-x-6">
              <a
                href="#"
                className="text-sm text-gray-400 hover:text-white transition"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-sm text-gray-400 hover:text-white transition"
              >
                Terms of Use
              </a>
              <a
                href="#"
                className="text-sm text-gray-400 hover:text-white transition"
              >
                Sitemap
              </a>
            </div>
          </div>

          {/* Back to top button */}
          <div className="fixed bottom-6 right-6 z-20">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="bg-yellow-500 hover:bg-yellow-600 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition transform hover:scale-110 animate-pulse-glow"
              aria-label="Back to top"
            >
              <ChevronUp size={20} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}